import { useCallback } from 'react';

import { IntlShape, useIntl } from 'react-intl';

import { IConfigOffer, ILimit } from 'generated/sanity-graphql';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { LoyaltyOffer } from 'state/loyalty/types';
import { useMenuContext } from 'state/menu';
import { useMainMenuContext } from 'state/menu/main-menu';
import { IValidDayPart } from 'state/menu/main-menu/types';
import { timeToString } from 'utils/availability';

import { getOfferNextAvailableDayPart, incentiveHasEvaluationError } from './utils';

type TConfigList = ReadonlyArray<Pick<IConfigOffer, '_id' | 'rules'> | null>;

const sortDayPartsByStartTime = (dayParts: ReadonlyArray<IValidDayPart>) => {
  return [...dayParts].sort((a, b) => {
    if (a.startTime < b.startTime) {
      return -1;
    }
    if (b.startTime < a.startTime) {
      return 1;
    }

    return 0;
  });
};

const getExceedsMaxRedemptionErrorMessage = (
  incentive: LoyaltyOffer,
  dayParts: ReadonlyArray<IValidDayPart>,
  { formatMessage, formatTime }: IntlShape,
  configOffers?: TConfigList,
  burgersForBreakfastEnabled?: boolean
) => {
  if (configOffers) {
    const configOffer = configOffers?.find(configOffer => configOffer?._id === incentive._id);

    if (configOffer) {
      const nextAvailableDayPart = getOfferNextAvailableDayPart(
        // If user has exceeded max redemptions (per day), we want to find the earliest available day part for the next available day
        sortDayPartsByStartTime(dayParts),
        incentive.daypart,
        {
          burgersForBreakfastEnabled,
        }
      );
      const limitRule = configOffer.rules?.find(
        (rule): rule is ILimit => rule?.__typename === 'Limit'
      );

      switch (limitRule?.interval) {
        case 'day':
          return nextAvailableDayPart
            ? formatMessage(
                {
                  id: 'availableAgainTomorrowAt',
                  defaultMessage: 'Available Again Tomorrow at {time}',
                },
                { time: timeToString(nextAvailableDayPart.startTime, formatTime) }
              )
            : formatMessage({
                id: 'availableAgainTomorrow',
                defaultMessage: 'Available Again Tomorrow',
              });
        default:
          return formatMessage({ id: 'offerNotAvailable' });
      }
    }

    return formatMessage({ id: 'offerNotAvailable' });
  }
  return formatMessage({ id: 'offerNotAvailable' });
};

export const useWalmartSeasonalPromoAvailabilityUtils = (configOffers?: TConfigList) => {
  const intl = useIntl();
  const { showBurgersForBreakfast: burgersForBreakfastEnabled } = useMenuContext();
  const { dayParts } = useMainMenuContext();

  const getIncentiveUnavailableMessage = useCallback(
    (incentive: LoyaltyOffer) => {
      if (
        incentiveHasEvaluationError(incentive, IncentiveEvaluationErrorCodes.EXCEEDS_MAX_REDEMPTION)
      ) {
        return getExceedsMaxRedemptionErrorMessage(
          incentive,
          dayParts,
          intl,
          configOffers,
          burgersForBreakfastEnabled
        );
      }

      // Empty string if no issues found
      return '';
    },
    [burgersForBreakfastEnabled, dayParts, intl, configOffers]
  );

  return { getIncentiveUnavailableMessage };
};
